import { useEffect, useState } from 'react';
import { formatPhoneNumber, type TtMatch, type TtNumber } from '@avslack/shared';
import { useQueryClient } from '@tanstack/react-query';
import { getTtNumbers, savePhoneContact, searchTtCustomers } from '../lib/api';
import { phoneContactsKey, phoneRecentKey, usePhoneContacts } from '../data/phone';
import { Icon } from './Icon';

/**
 * A TT CIMJEGYZEKE a sajatunk mellett.
 *
 * ─── Miert kell? ────────────────────────────────────────────────────────────
 *
 * A sajat cimjegyzekunk csak azt ismeri, akivel MAR beszeltunk, vagy akit
 * valaki kezzel felvett. Az ugyfelek szamai viszont a TT-ben vannak - 576
 * rendelo, kozponti szamokkal, mobilokkal, munkatarsakkal. Ha valakit fel
 * akarunk hivni, aki meg sosem hivott minket, eddig at kellett menni a TT-be
 * a szamert.
 *
 * ─── Miert csak keresesre? ──────────────────────────────────────────────────
 *
 * Mert 576 ugyfelet nem listazunk ki, es mert a szamok lekerese ugyfelenkent
 * ket TT-hivas. Eloszor az ugyfelet keressuk meg (egy hivas), es csak amikor
 * a kolléga rakattint, kerjuk le a szamait.
 */
export function TtDirectory({
  query,
  canCallOut,
  busy,
  onDial,
}: {
  /** A cimjegyzek kereso mezojenek tartalma. */
  query: string;
  canCallOut: boolean;
  busy: boolean;
  onDial: (number: string) => void;
}) {
  const [matches, setMatches] = useState<TtMatch[]>([]);
  const [state, setState] = useState<'idle' | 'searching' | 'down' | 'empty'>('idle');
  const needle = query.trim();

  /*
    Gepeles kozben nem kerdezunk: 400 ms szunet utan indul a kereses. Enelkul
    egy "Alpha-Vet" begepelese kilenc TT-hivast jelentene.
  */
  useEffect(() => {
    if (needle.length < 3) {
      setMatches([]);
      setState('idle');
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setState('searching');
      void searchTtCustomers(needle)
        .catch(() => null)
        .then((result) => {
          if (cancelled) return;
          if (!result) return setState('down');
          setMatches(result.matches);
          setState(result.ttDown ? 'down' : result.matches.length === 0 ? 'empty' : 'idle');
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [needle]);

  // Rovid keresesnel meg nem szolunk bele - a sajat cimjegyzek eleg.
  if (needle.length < 3) return null;

  return (
    <div className="mt-2 border-t border-border-soft pt-2">
      <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-faint">
        TT ügyfelek
      </p>
      {state === 'searching' && <p className="text-xs text-ink-muted">Keresés a TT-ben…</p>}
      {state === 'down' && <p className="text-xs text-ink-muted">A TT most nem válaszol.</p>}
      {state === 'empty' && <p className="text-xs text-ink-muted">Nincs találat a TT-ben.</p>}
      {matches.length > 0 && (
        <ul className="max-h-64 divide-y divide-border-soft overflow-y-auto">
          {matches.map((match) => (
            <TtCustomerRow
              key={match.clinicId}
              match={match}
              canCallOut={canCallOut}
              busy={busy}
              onDial={onDial}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TtCustomerRow({
  match,
  canCallOut,
  busy,
  onDial,
}: {
  match: TtMatch;
  canCallOut: boolean;
  busy: boolean;
  onDial: (number: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [numbers, setNumbers] = useState<TtNumber[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const toggle = async () => {
    setOpen((current) => !current);
    if (numbers || loading) return;
    setLoading(true);
    setFailed(false);
    // A sikertelen lekeres NEM "nincs szam": kulon jelezzuk, es a kovetkezo
    // kinyitas ujraprobalja (a `numbers` null marad).
    const result = await getTtNumbers(match.clinicId).catch(() => null);
    if (result) setNumbers(result);
    else setFailed(true);
    setLoading(false);
  };

  const inactive = match.allapot && match.allapot !== 'Aktív';

  return (
    <li className="py-1.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => void toggle()}
        className="flex w-full items-center gap-2 text-left"
      >
        <Icon name={open ? 'nyilLe' : 'nyilJobbra'} size="sm" className="shrink-0 text-ink-faint" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{match.ceg ?? match.rendelo}</span>
          <span className="block truncate text-2xs text-ink-faint">
            {[match.telepules, inactive ? match.allapot : null].filter(Boolean).join(' · ')}
          </span>
        </span>
      </button>

      {open && (
        <div className="mt-1 pl-6">
          {loading && <p className="text-2xs text-ink-faint">Számok betöltése…</p>}
          {failed && !loading && (
            <p role="alert" className="text-2xs text-danger">
              Nem sikerült lekérni a számokat a TT-ből. Nyisd ki újra a próbához.
            </p>
          )}
          {numbers?.length === 0 && !loading && (
            <p className="text-2xs text-ink-faint">Ehhez az ügyfélhez nincs szám a TT-ben.</p>
          )}
          <ul className="space-y-1">
            {(numbers ?? []).map((entry) => (
              <TtNumberRow
                key={entry.number}
                entry={entry}
                match={match}
                canCallOut={canCallOut}
                busy={busy}
                onDial={onDial}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function TtNumberRow({
  entry,
  match,
  canCallOut,
  busy,
  onDial,
}: {
  entry: TtNumber;
  match: TtMatch;
  canCallOut: boolean;
  busy: boolean;
  onDial: (number: string) => void;
}) {
  const queryClient = useQueryClient();
  const { entryOf } = usePhoneContacts();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const known = Boolean(entryOf(entry.number));

  /*
    "Felveszem": a szam bekerul a SAJAT cimjegyzekunkbe, a ceggel es a
    TT-ugyfellel egyutt. Ettol a kesobbi hivasoknal - es visszamenoleg a
    korabbiaknal is - nevvel latszik, TT-kerdes nelkul.
  */
  const save = async () => {
    setSaving(true);
    setError(false);
    try {
      const contacts = await savePhoneContact(entry.number, entry.owner ?? '', {
        companyName: match.ceg ?? match.rendelo ?? '',
        ttClinicId: match.clinicId,
      });
      queryClient.setQueryData(phoneContactsKey, contacts);
      void queryClient.invalidateQueries({ queryKey: phoneRecentKey });
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs tabular-nums">
            {formatPhoneNumber(entry.number)}
          </span>
          <span className="block truncate text-2xs text-ink-faint">
            {[entry.owner, entry.label].filter(Boolean).join(' · ')}
          </span>
        </span>
        {known ? (
          <span className="shrink-0 text-2xs text-ink-faint">a címjegyzékben</span>
        ) : (
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            aria-label={`${formatPhoneNumber(entry.number)} felvétele a címjegyzékbe`}
            className="shrink-0 rounded-control border border-border px-1.5 py-0.5 text-2xs text-ink-muted hover:text-ink disabled:opacity-50"
          >
            Felveszem
          </button>
        )}
        {canCallOut && (
          <button
            type="button"
            onClick={() => onDial(entry.number)}
            disabled={busy}
            className="shrink-0 rounded-control bg-accent px-2 py-0.5 text-2xs font-semibold text-accent-ink disabled:opacity-50"
          >
            <Icon name="telefon" size="sm" /> Hívás
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-0.5 text-2xs text-danger">
          Nem sikerült felvenni a címjegyzékbe. Próbáld újra.
        </p>
      )}
    </li>
  );
}
